import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { Plus, Trash2, Save, RefreshCw } from 'lucide-react';
import './CompanyAssignments.css';

// What a company's own administrator uses to give their people a dashboard and
// choose which machines appear on it.
//
// Nothing here can reach outside the company: the server takes the company from
// the caller's own account, and the database refuses a dashboard the company was
// not granted or a machine it does not own - so a mistake here is refused rather
// than silently crossing a boundary.
const CompanyAssignments = ({ users, dashboards, onChanged }) => {
  const [assignments, setAssignments] = useState([]);
  const [picking, setPicking] = useState(null);      // user_dashboard_id being edited
  const [machineView, setMachineView] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [form, setForm] = useState({ user_id: '', dashboard_id: '' });
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const load = async () => {
    try {
      const res = await api.get('/company/assignments');
      setAssignments(res.data);
    } catch (e) {
      setError('Could not load the assignments');
    }
  };

  useEffect(() => { load(); }, []);

  const give = async (e) => {
    e.preventDefault();
    if (!form.user_id || !form.dashboard_id) return;
    setBusy('new'); setError(null); setMessage(null);
    try {
      await api.post(`/company/users/${form.user_id}/dashboards`, { dashboard_id: form.dashboard_id });
      setMessage('Dashboard given. Now choose which machines they see on it.');
      setForm({ user_id: '', dashboard_id: '' });
      await load();
      if (onChanged) onChanged();
    } catch (e2) {
      setError(e2.response?.data?.error || 'Could not give that dashboard');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (id) => {
    setBusy(id); setError(null); setMessage(null);
    try {
      await api.delete(`/company/assignments/${id}`);
      if (picking === id) { setPicking(null); setMachineView(null); }
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not remove that');
    } finally {
      setBusy(null);
    }
  };

  const openMachines = async (id) => {
    if (picking === id) { setPicking(null); setMachineView(null); return; }
    setBusy(id); setError(null);
    try {
      const res = await api.get(`/company/assignments/${id}/machines`);
      setMachineView(res.data);
      setChosen(res.data.machines.filter((m) => m.assigned).map((m) => m.tb_device_id));
      setPicking(id);
    } catch (e) {
      setError('Could not load the machines');
    } finally {
      setBusy(null);
    }
  };

  const toggle = (id) => {
    setChosen((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : prev.concat(id)));
  };

  const saveMachines = async () => {
    setBusy(picking); setError(null); setMessage(null);
    try {
      const res = await api.put(`/company/assignments/${picking}/machines`, { device_ids: chosen });
      setMessage(`${res.data.count} machine${res.data.count === 1 ? '' : 's'} on that dashboard.`);
      setPicking(null); setMachineView(null);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save those machines');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="assignments">
      <div className="section-header">
        <h2>Who sees what</h2>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      <form className="assign-new" onSubmit={give}>
        <div className="form-group">
          <label htmlFor="assign-user">Person</label>
          <select
            id="assign-user"
            value={form.user_id}
            onChange={(e) => setForm({ ...form, user_id: e.target.value })}
            required
          >
            <option value="">Choose...</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.username}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="assign-dashboard">Dashboard</label>
          <select
            id="assign-dashboard"
            value={form.dashboard_id}
            onChange={(e) => setForm({ ...form, dashboard_id: e.target.value })}
            required
          >
            <option value="">Choose...</option>
            {dashboards.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        </div>

        <button className="btn btn-primary" type="submit" disabled={busy === 'new'}>
          <Plus size={16} /> Give
        </button>
      </form>

      {dashboards.length === 0 && (
        <p className="form-help">
          Your company has no dashboards yet. PrimeHive grants those.
        </p>
      )}

      <div className="table-scroll">
        <table className="people-table">
          <thead>
            <tr>
              <th>Person</th>
              <th>Dashboard</th>
              <th>Machines</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {assignments.map((a) => (
              <tr key={a.user_dashboard_id}>
                <td>{a.username}</td>
                <td>{a.dashboard_name}</td>
                <td>{a.machine_count}</td>
                <td className="row-actions">
                  <button
                    className="btn btn-secondary btn-small"
                    disabled={busy === a.user_dashboard_id}
                    onClick={() => openMachines(a.user_dashboard_id)}
                  >
                    <RefreshCw size={14} /> Machines
                  </button>
                  <button
                    className="btn btn-danger btn-small"
                    disabled={busy === a.user_dashboard_id}
                    onClick={() => remove(a.user_dashboard_id)}
                  >
                    <Trash2 size={14} /> Remove
                  </button>
                </td>
              </tr>
            ))}
            {assignments.length === 0 && (
              <tr><td colSpan={4} className="empty">Nobody has a dashboard yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {picking && machineView && (
        <div className="machine-picker">
          <h3>Machines on {machineView.dashboard_name}</h3>
          <p className="form-help">
            {machineView.accepts.length
              ? `This dashboard reads ${machineView.accepts.join(', ')} machines. Others are shown but cannot be chosen.`
              : 'This dashboard has no machine type set, so any machine can be chosen.'}
          </p>

          <div className="machine-list">
            {machineView.machines.map((m) => (
              <label
                key={m.tb_device_id}
                className={`machine-row ${m.fits ? '' : 'machine-unfit'}`}
              >
                <input
                  type="checkbox"
                  checked={chosen.includes(m.tb_device_id)}
                  disabled={!m.fits}
                  onChange={() => toggle(m.tb_device_id)}
                />
                <span className="machine-name">
                  {m.label || m.device_name}
                  {m.label && <span className="machine-ref">{m.device_name}</span>}
                </span>
                <span className="machine-profile">{m.profile || 'unknown type'}</span>
                {!m.fits && <span className="machine-note">not this kind of machine</span>}
              </label>
            ))}
            {machineView.machines.length === 0 && (
              <p className="form-help">No machines yet. Use Refresh on the Machines list.</p>
            )}
          </div>

          <div className="machine-actions">
            <button className="btn btn-primary" onClick={saveMachines} disabled={busy === picking}>
              <Save size={16} /> Save
            </button>
            <button
              className="btn btn-secondary"
              onClick={() => { setPicking(null); setMachineView(null); }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyAssignments;
